import { Box, useTheme } from "@modernatx-internal/component-library";
import EventEmitter from "events";
import React, { startTransition, useContext } from "react";

import { useExperience } from "@/context/ExperienceContext";
import { targetProviderState } from "@/hooks/useTargetProvider";

type StickyState = Record<
  string,
  {
    bottom: number;
    bottomOffset: number;
    height: number;
    layer: string;
    peekabooHiding: boolean;
    top: number;
  }
>;

type StickyOffset =
  | number
  | [number | null, number | null]
  | [number | null, number | null, number | null];
type BoxSxTop = NonNullable<React.ComponentProps<typeof Box>["sx"]>["top"];

interface StickyContextValue {
  stickyLayer: string;
  stickyLayerParents: string[];
  stickyState: React.MutableRefObject<StickyState>;
  stickyStateDelete: (stickyId: string) => void;
  stickyStateEmitter: EventEmitter;
  stickyStateSet: (state: StickyState) => void;
}

const StickyContext = React.createContext<StickyContextValue>({
  stickyLayer: "",
  stickyLayerParents: [],
  stickyState: { current: {} },
  stickyStateDelete: () => {},
  stickyStateEmitter: new EventEmitter(),
  stickyStateSet: () => {}
});

const StickingContext = React.createContext({
  sticking: false
});

export const useSticking = () => React.useContext(StickingContext);

function getOffsetTopRelativeToDocument(el: HTMLElement | null): number {
  let offsetTop = 0;
  while (el) {
    offsetTop += el.offsetTop;
    const parent = el.offsetParent;
    if (parent instanceof HTMLElement) {
      el = parent;
    } else {
      break;
    }
  }
  return offsetTop;
}

function resolveStickyOffset(
  offset: StickyOffset,
  breakpoints: readonly string[],
  viewportWidth: number
): number {
  if (!Array.isArray(offset)) {
    return offset;
  }

  let resolved = offset[0] ?? 0;

  offset.slice(1).forEach((value, index) => {
    const breakpoint = parseInt(breakpoints[index] || "0", 10);

    if (viewportWidth >= breakpoint && value !== null) {
      resolved = value;
    }
  });

  return resolved;
}

export const StickyProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { stickyLayer, stickyLayerParents, stickyState, stickyStateEmitter } =
    useContext(StickyContext);
  const layer = React.useId();
  const stickyStateDelete = React.useCallback(
    (id: string) => {
      delete stickyState.current[id];
      stickyStateEmitter.emit("change", stickyState.current);
    },
    [stickyState, stickyStateEmitter]
  );
  const stickyStateSet = React.useCallback(
    (stickyStateNext: StickyState) => {
      stickyState.current = { ...stickyState.current, ...stickyStateNext };
      stickyStateEmitter.emit("change", stickyState.current);
    },
    [stickyState, stickyStateEmitter]
  );

  return (
    <StickyContext.Provider
      value={{
        stickyLayer: layer,
        stickyLayerParents: [...stickyLayerParents, ...(stickyLayer ? [stickyLayer] : [])],
        stickyState,
        stickyStateDelete,
        stickyStateEmitter,
        stickyStateSet
      }}
    >
      {children}
    </StickyContext.Provider>
  );
};

export const Sticky: React.FC<
  React.ComponentProps<typeof Box> & {
    bottom?: StickyOffset;
    disabled?: boolean;
    markerAs?: React.ComponentProps<typeof Box>["as"];
    peekaboo?: boolean;
    top?: StickyOffset;
  }
> = ({
  bottom: bottomConfiguredInitial = 0,
  children,
  disabled: disabledInitial,
  sx,
  markerAs,
  peekaboo,
  top: topConfiguredInitial = 0,
  ...other
}) => {
  const {
    stickyLayer,
    stickyLayerParents,
    stickyState,
    stickyStateDelete,
    stickyStateEmitter,
    stickyStateSet
  } = React.useContext(StickyContext);
  const theme = useTheme();
  const { screenshot } = useExperience();
  const disabled = screenshot || disabledInitial;
  const id = React.useId();
  const [bottomConfigured, bottomConfiguredSet] = React.useState(() =>
    resolveStickyOffset(bottomConfiguredInitial, theme.breakpoints, 0)
  );
  const [topConfigured, topConfiguredSet] = React.useState(() =>
    resolveStickyOffset(topConfiguredInitial, theme.breakpoints, 0)
  );
  const scrollY = React.useRef<number>(0);
  const el = React.useRef<HTMLElement>(null);
  const markerNext = React.useRef<HTMLElement>(null);
  const markerPrev = React.useRef<HTMLElement>(null);
  const [peekabooVisible, peekabooVisibleSet] = React.useState(true);
  const [sticking, stickingSet] = React.useState(false);
  const [state, setState] = React.useState<StickyState>(stickyState.current);
  const sticky = state[id];
  const layersAssociated = [...stickyLayerParents, stickyLayer];
  const stickiesSorted = Object.entries(state).sort(
    (prev, next) => prev[1].bottom - next[1].bottom
  );
  const zIndex = [...stickiesSorted].reverse().findIndex(([key]) => key === id);
  const topComputed = stickiesSorted
    .filter(
      ([key, { bottom, layer, peekabooHiding, top }]) =>
        !peekabooHiding &&
        key !== id &&
        layersAssociated.includes(layer) &&
        (sticky?.bottom || 0) + (sticky?.top || 0) > bottom + top
    )
    .reduce(
      (acc, [, { bottomOffset, height, top }]) => acc + height + top + bottomOffset,
      topConfigured
    );
  const topOffset = topComputed - topConfigured;
  const renderResponsiveTop = Array.isArray(topConfiguredInitial) && !peekaboo;
  const topResponsive = renderResponsiveTop
    ? (topConfiguredInitial.map((value) =>
        value === null ? null : value + topOffset
      ) as StickyOffset)
    : topConfiguredInitial;
  const topResponsiveStyle = (Array.isArray(topResponsive)
    ? topResponsive.map((value) => (value === null ? null : `${value}px`))
    : `${topResponsive}px`) as unknown as BoxSxTop;

  const handleSetSticky = React.useCallback(() => {
    if (el.current && markerNext.current) {
      const nextOffsetTop = getOffsetTopRelativeToDocument(markerNext.current);

      stickyStateSet({
        [id]: {
          bottom: nextOffsetTop + el.current.offsetHeight,
          bottomOffset: bottomConfigured,
          height: el.current.offsetHeight,
          layer: stickyLayer,
          peekabooHiding: !!(peekaboo && !peekabooVisible),
          top: topConfigured
        }
      });
    }
  }, [bottomConfigured, id, peekaboo, peekabooVisible, stickyLayer, stickyStateSet, topConfigured]);

  // This effect has to come first so that it captures all of the
  // changes to the sticky state that comes from mounted components
  React.useEffect(() => {
    const handleChange = (newState: StickyState) => {
      startTransition(() => {
        setState(newState);
      });
    };
    stickyStateEmitter.on("change", handleChange);

    return () => {
      stickyStateEmitter.off("change", handleChange);
    };
  }, [stickyStateEmitter]);

  const handleScroll = React.useCallback(() => {
    if (markerPrev.current) {
      const prevOffsetTop = markerPrev.current?.getBoundingClientRect().top;
      const offsetTop = (prevOffsetTop || 0) - topComputed;
      startTransition(() => {
        stickingSet(offsetTop <= 0);
      });
    }

    if (peekaboo && sticky && !disabled) {
      const scrollYNext = globalThis.scrollY;
      const isAtTop = scrollYNext - sticky.bottom <= 0;

      // If the scroll was initiated from the TargetProvider, ignore it
      if (targetProviderState.isScrolling) {
        peekabooVisibleSet(false);
        handleSetSticky();
      } else if (
        (isAtTop || scrollYNext < scrollY.current) &&
        !peekabooVisible &&
        !targetProviderState.isScrolling
      ) {
        peekabooVisibleSet(true);
        handleSetSticky();
      } else if (!isAtTop && scrollYNext > scrollY.current && peekabooVisible) {
        peekabooVisibleSet(false);
        handleSetSticky();
      }

      scrollY.current = scrollYNext;
    }
  }, [disabled, handleSetSticky, peekaboo, peekabooVisible, sticky, topComputed]);

  React.useEffect(() => {
    if (!disabled) {
      window.addEventListener("scroll", handleScroll);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [disabled, handleScroll]);

  React.useEffect(() => {
    if (!disabled) {
      handleSetSticky();

      window.addEventListener("resize", handleSetSticky);
    }

    return () => {
      window.removeEventListener("resize", handleSetSticky);
      stickyStateDelete(id);
    };
  }, [id, disabled, handleSetSticky, stickyStateDelete]);

  React.useEffect(() => {
    const hasResponsiveBottom = Array.isArray(bottomConfiguredInitial);
    const hasResponsiveTop = Array.isArray(topConfiguredInitial);

    if (!hasResponsiveBottom) {
      bottomConfiguredSet(bottomConfiguredInitial);
    }

    if (!hasResponsiveTop) {
      topConfiguredSet(topConfiguredInitial);
    }

    if (!hasResponsiveBottom && !hasResponsiveTop) {
      return;
    }

    const handleResize = () => {
      const viewportWidth = window.innerWidth;

      if (hasResponsiveBottom) {
        bottomConfiguredSet(
          resolveStickyOffset(bottomConfiguredInitial, theme.breakpoints, viewportWidth)
        );
      }

      if (hasResponsiveTop) {
        topConfiguredSet(
          resolveStickyOffset(topConfiguredInitial, theme.breakpoints, viewportWidth)
        );
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [bottomConfiguredInitial, theme.breakpoints, topConfiguredInitial]);

  return (
    <>
      <Box ref={markerPrev} as={markerAs} />
      <Box
        {...other}
        ref={el}
        data-sticking={sticking}
        data-sticky={id}
        sx={{
          zIndex: ({ zIndices }) => zIndices.navbar + zIndex,
          ...sx,
          top: disabled
            ? "initial"
            : renderResponsiveTop
              ? topResponsiveStyle
              : () =>
                  peekaboo && !peekabooVisible
                    ? `${topComputed - (sticky?.height || 0)}px`
                    : `${topComputed}px`,
          transition: () => "top 400ms cubic-bezier(0.4, 0, 0, 1)",
          position: disabled ? "relative" : "sticky"
        }}
      >
        <StickingContext.Provider value={{ sticking }}>{children}</StickingContext.Provider>
      </Box>
      <Box ref={markerNext} as={markerAs} />
    </>
  );
};
